import { useTranslation } from 'react-i18next'
import { Modal } from './Modal'
import { Button } from './Button'
import { AlertTriangle, Info, AlertCircle } from 'lucide-react'

export interface ConfirmDialogProps {
  isOpen: boolean
  onClose: () => void
  onConfirm: () => void
  title: string
  message: string
  confirmLabel?: string
  cancelLabel?: string
  variant?: 'error' | 'warning' | 'info'
  loading?: boolean
}

const variantConfig = {
  error: { icon: AlertCircle, iconClass: 'text-error', variant: 'error' as const },
  warning: { icon: AlertTriangle, iconClass: 'text-warning', variant: 'warning' as const },
  info: { icon: Info, iconClass: 'text-info', variant: 'info' as const },
}

export function ConfirmDialog({
  isOpen,
  onClose,
  onConfirm,
  title,
  message,
  confirmLabel,
  cancelLabel,
  variant = 'error',
  loading = false,
}: ConfirmDialogProps) {
  const { t } = useTranslation('common')
  const resolvedConfirm = confirmLabel ?? t('actions.confirm')
  const resolvedCancel = cancelLabel ?? t('actions.cancel')
  const config = variantConfig[variant]
  const Icon = config.icon

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="sm">
      <div className="flex flex-col items-center text-center">
        <div className={`mb-4 ${config.iconClass}`}>
          <Icon className="w-12 h-12" />
        </div>
        <h3 className="font-bold text-lg mb-2">{title}</h3>
        <p className="text-base-content/70">{message}</p>
      </div>
      <div className="modal-action flex gap-2 w-full sm:justify-center sm:w-auto">
        <Button variant="outline" className="flex-1 sm:flex-initial" onClick={onClose} disabled={loading}>
          {resolvedCancel}
        </Button>
        <Button variant={config.variant} className="flex-1 sm:flex-initial" onClick={onConfirm} loading={loading}>
          {resolvedConfirm}
        </Button>
      </div>
    </Modal>
  )
}
